import React, { useEffect, useState, useRef } from "react";
import {
  usePDFShowHide,
  registerTableForPDF,
  unregisterTableForPDF,
} from "../../hooks/usePDFShowHide";

/**
 * TableLoadingWrapper - A wrapper component that tracks table loading state for PDF generation
 */
const TableLoadingWrapper = ({
  children,
  tableId,
  className = "",
  minLoadTime = 2000,
  maxLoadTime = 15000,
  checkInterval = 500,
  ...props
}) => {
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [, , isPDFMode] = usePDFShowHide();
  const checkingRef = useRef(false);
  const timersRef = useRef({ min: null, max: null, check: null });

  const clearAllTimers = () => {
    if (timersRef.current.min) clearTimeout(timersRef.current.min);
    if (timersRef.current.max) clearTimeout(timersRef.current.max);
    if (timersRef.current.check) clearTimeout(timersRef.current.check);
    timersRef.current = { min: null, max: null, check: null };
  };

  const checkTableContent = () => {
    if (!isPDFMode || checkingRef.current === false) return;

    const wrapper = document.getElementById(`table-wrapper-${tableId}`);
    if (!wrapper) {
      setTableLoaded();
      return;
    }

    const table = wrapper.querySelector("table");
    if (!table) {
      setTableLoaded();
      return;
    }

    // Check for loading indicators first
    const loadingElements = wrapper.querySelectorAll(
      '.loading, .spinner, [data-loading="true"], [data-pdf-loading="true"]'
    );

    if (loadingElements.length > 0) {
      // Still has loading indicators, keep checking
      timersRef.current.check = setTimeout(checkTableContent, checkInterval);
      return;
    }

    const tbody = table.querySelector("tbody");
    if (!tbody) {
      setTableLoaded();
      return;
    }

    const rows = tbody.querySelectorAll("tr");
    if (rows.length === 0) {
      // No rows at all, keep checking
      timersRef.current.check = setTimeout(checkTableContent, checkInterval);
      return;
    }

    const hasDataRows = Array.from(rows).some((row) => {
      const cells = row.querySelectorAll("td");
      if (cells.length === 0) return false;

      return Array.from(cells).some((cell) => {
        const text = cell.textContent.trim();
        // More comprehensive check for actual data
        return (
          text !== "" &&
          text !== "0" &&
          text !== "-" &&
          text !== "N/A" &&
          text !== "Loading..." &&
          text !== "No data" &&
          text !== "..." &&
          !text.toLowerCase().includes("loading") &&
          !text.toLowerCase().includes("no data") &&
          !text.toLowerCase().includes("empty")
        );
      });
    });

    if (hasDataRows) {
      setTableLoaded();
    } else {
      // Keep checking
      timersRef.current.check = setTimeout(checkTableContent, checkInterval);
    }
  };

  const setTableLoaded = () => {
    if (!checkingRef.current) return;

    setIsTableLoading(false);
    registerTableForPDF(tableId, false);
    checkingRef.current = false;
    clearAllTimers();
  };

  useEffect(() => {
    if (isPDFMode && tableId) {
      checkingRef.current = true;
      registerTableForPDF(tableId, true);
      setIsTableLoading(true);

      // Set minimum loading time - don't mark as loaded until this passes
      timersRef.current.min = setTimeout(() => {
        if (checkingRef.current) {
          checkTableContent();
        }
      }, minLoadTime);

      // Set maximum loading time - force complete after this
      timersRef.current.max = setTimeout(() => {
        if (checkingRef.current) {
          console.warn(
            `Table ${tableId} reached max load time, marking as complete`
          );
          setTableLoaded();
        }
      }, maxLoadTime);

      return () => {
        checkingRef.current = false;
        clearAllTimers();
        unregisterTableForPDF(tableId);
      };
    } else {
      checkingRef.current = false;
      setIsTableLoading(false);
      clearAllTimers();
    }
  }, [isPDFMode, tableId, minLoadTime, maxLoadTime]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      checkingRef.current = false;
      clearAllTimers();
      if (tableId) {
        unregisterTableForPDF(tableId);
      }
    };
  }, [tableId]);

  return (
    <div
      id={`table-wrapper-${tableId}`}
      className={`table-loading-wrapper ${className}`}
      data-table-id={tableId}
      data-pdf-loading={isPDFMode && isTableLoading ? "true" : "false"}
      style={{ position: "relative" }}
      {...props}
    >
      {children}
      {isPDFMode && isTableLoading && (
        <div
          className="pdf-table-loading-indicator"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            padding: "10px 20px",
            borderRadius: "4px",
            border: "1px solid #ddd",
            zIndex: 1000,
            fontSize: "12px",
            color: "#666",
            whiteSpace: "nowrap",
          }}
        >
          📊 Loading table data for PDF...
        </div>
      )}
    </div>
  );
};

export default TableLoadingWrapper;
